import { FileText, Download, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/others/Badges";
import { Button } from "@/components/ui/button";
import { getFileTypeColor } from "@/utils/convetors";
import { useAppDispatch } from "@/stores/hook";
import { setEditQualificationData } from "@/module/education/stores/educationSlice";

interface QualificationCardProps {
  data: any;
}

const MONTH_NAMES = [
  "january","february","march","april","may","june",
  "july","august","september","october","november","december",
];

const getMonthName = (month: string | number) => {
  if (!month) return "";
  const num = Number(month);
  if (!isNaN(num) && num >= 1 && num <= 12) {
    return new Date(2000, num - 1).toLocaleString("default", { month: "short" });
  }
  const lower = String(month).trim().toLowerCase();
  const idx = MONTH_NAMES.findIndex((m) => m.startsWith(lower.slice(0, 3)));
  if (idx >= 0) {
    return new Date(2000, idx).toLocaleString("default", { month: "short" });
  }
  return String(month);
};

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

export default function QualificationCard({ data }: QualificationCardProps) {
  const dispatch = useAppDispatch();
  const {
    qualificationLevel,
    specialization,
    nativeUniversity,
    monthOfPassout,
    yearOfPassout,
    minScore,
    documents,
  } = data;

  const level = qualificationLevel;
  // Web-form qualifications may skip any detail: month/year/score can be
  // null — show what there is, and "—" for a blank score ("no score").
  const formattedDate = [getMonthName(monthOfPassout), yearOfPassout ?? ""]
    .filter(Boolean)
    .join(" ");
  const hasScore = minScore !== null && minScore !== undefined && minScore !== "";

  return (
    <div
      className="border border-[#e6e8e7] rounded-[16px] p-3 flex flex-col gap-4 w-full transition-all hover:border-[#46a580]/30"
      style={{
        backgroundImage:
          "linear-gradient(175.983deg, rgb(237, 250, 245) 11.475%, rgb(255, 255, 255) 52.352%)",
      }}
    >
      {/* Header Section */}
      <div className="flex items-start justify-between gap-2 w-full">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {/* Qualification Name (e.g. 10th Standard) */}
            <p className="text-[14px] font-medium text-[#051912] tracking-[-0.56px] leading-normal whitespace-nowrap">
              {level?.label || "Qualification"}
            </p>

            <div className="h-[12px] w-px bg-[#CDD1D0] shrink-0" />

            <div className="flex items-center gap-1.5 shrink-0">
              {/* Stream / Specialization */}
              <p className="text-[12px] font-medium text-[#505e59] tracking-[-0.24px] leading-normal whitespace-nowrap">
                {specialization?.name}
              </p>

              <div className="size-1 rounded-full bg-[#505e59] shrink-0" />

              {/* University / Board */}
              <p className="text-[12px] font-normal text-[#505e59] tracking-[-0.24px] leading-normal whitespace-nowrap max-w-[150px] truncate">
                {nativeUniversity}
              </p>

              <div className="size-1 rounded-full bg-[#505e59] shrink-0" />

              {/* Date */}
              <p className="text-[12px] font-normal text-[#505e59] tracking-[-0.24px] leading-normal whitespace-nowrap">
                {formattedDate}
              </p>
            </div>
          </div>
        </div>

        {/* Score Badge + Edit */}
        <div className="flex items-center justify-end shrink-0 gap-1.5">
          <Badge className="bg-[#46a580] text-white text-[14px] font-normal tracking-[-0.56px] leading-[1.35] px-2 py-1 rounded-full h-[22px] flex items-center gap-1 hover:bg-[#3d9170]">
            <span className="opacity-90">Score:</span>
            <span className="font-medium">{hasScore ? `${minScore}%` : "—"}</span>
          </Badge>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => dispatch(setEditQualificationData(data))}
            className="size-[24px] p-1 rounded-full hover:bg-slate-200/50"
          >
            <Pencil className="size-[13px] text-[#505e59]" strokeWidth={1.8} />
          </Button>
        </div>
      </div>

      {/* Documents Section */}
      {documents && documents.length > 0 && (
        <div className="flex flex-wrap gap-2 items-start">
          {documents.map((doc: any) => (
            <div
              key={doc.id}
              className="bg-[rgba(40,12,242,0.02)] border border-[rgba(40,12,242,0.06)] rounded-[12px] px-3 py-2 flex items-center gap-2 flex-1 min-w-[200px] max-w-full"
            >
              {/* File Icon */}
              <div className="relative size-6 shrink-0">
                <FileText className="size-6 text-[#cacfd8]" strokeWidth={1.5} />
                <Badge
                  variant="fileIcon"
                  className={`${getFileTypeColor(doc?.type)}`}
                >
                  {doc?.type}
                </Badge>
              </div>

              {/* Document Name */}
              <div className="flex-1 min-w-0 flex flex-col">
                <p
                  className="text-[12px] font-medium text-[#051912] tracking-[-0.48px] leading-normal truncate"
                  title={doc.fileName}
                >
                  {doc.fileName}
                </p>
                <span className="text-[10px] text-slate-400">
                  {formatFileSize(doc.size)}
                </span>
              </div>

              {/* Download Button */}
              <a
                href={doc.fileUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-shrink-0"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-[24px] p-1 shrink-0 border-none hover:bg-slate-200/50 rounded-full"
                >
                  <Download
                    className="size-[14px] text-[#374741]"
                    strokeWidth={2}
                  />
                </Button>
              </a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
